import React, { useEffect, useState } from 'react';
import Modal from 'components/Modal';
import { Alert, Button, Card, Col, Form, Result, Row, Space } from 'antd';
import { gql } from '@apollo/client';
import { useMutation } from 'hooks/apollo';
import useGlobalContext from 'hooks/useGlobalContext';
import useUser from 'hooks/useUser';
import Order from 'components/Order';
import { createOrder } from 'shared/utils/order';
import HubspotStatus from 'components/HubspotStatus';
import { DataSourceType } from 'shared/services/prisma/types';
import useDataSource from 'hooks/useDataSource';
import SetupPaymentButton from 'components/SetupPaymentButton';
import LegacyLicenseForm from './LegacyLicenseForm';
import ModuleLicenseForm from './ModuleLicenseForm';
import OrderForm from './OrderForm';
import ModuleLicenseV2Form from './ModuleLicenseV2Form';

const CHANGE_LICENSE = gql`
  mutation($input: DataSourceLicenseInput!, $createHubspotDeal: Boolean, $orderConfirmation: Boolean) {
    changeDataSourceLicense(input: $input, createHubspotDeal: $createHubspotDeal, orderConfirmation: $orderConfirmation) {
      id
      startDate
      endDate
      renewalDate
      interval
    }
  }
`;

const DEFAULT_LICENSE_TYPE = {
  [DataSourceType.Cx]: 'v1',
  [DataSourceType.Website]: 'v2',
};

const ChangeLicense = ({ open, onCancel, onChange }) => {
  const { dataSource, loading } = useDataSource({ extraFields: 'owner { id email } hubspotDealRecordId customer { id paymentType }' });
  const { isSuperUser } = useUser();
  const minStep = isSuperUser ? 1 : 2;
  const [step, setStep] = useState(minStep);
  const [licenseForm] = Form.useForm();
  const [orderForm] = Form.useForm();
  const { message } = useGlobalContext();
  const [changeLicense, { loading: changeLicenseLoading }] = useMutation(CHANGE_LICENSE);
  const [licenseType, setLicenseType] = useState();
  const [licenseValues, setLicenseValues] = useState(null);
  const [formOrder, setFormOrder] = useState(null);

  useEffect(() => {
    if (open) {
      setStep(minStep);
      setLicenseType(DEFAULT_LICENSE_TYPE[dataSource?.type]);
    }
  }, [open]);

  const onLicenseTypeSelect = (type) => {
    setLicenseType(type);
    setStep(2);
  };

  const onLicenseChanged = (values) => {
    setLicenseValues(values);
    setFormOrder(createOrder({ ...values, dataSource }));
  };

  const goBack = () => {
    setStep(step - 1);
  };

  const onCustomOrder = () => {
    setStep(3);
  };

  const onSubmit = async (values, order) => {
    if (!order) {
      order = formOrder;
    }
    if (order.amount != null) {
      try {
        const result = await changeLicense({
          variables: {
            input: {
              dataSourceId: dataSource.id,
              licenseId: values.licenseId,
              interval: values.interval,
              startDate: values.startDate,
              modules: values.modules,
              settings: values.settings,
              order,
            },
            createHubspotDeal: values.createHubspotDeal,
            orderConfirmation: values.orderConfirmation,
          },
        });
        if (result.data.changeDataSourceLicense) {
          setStep(4);
          onChange();
        } else {
          message.error('Something went wrong, try again later.');
        }
      } catch (error) {
        message.error(error);
      }
    } else {
      message.info('License combination is not valid');
    }
  };

  const onCustomOrderSubmit = async (values) => {
    onSubmit(licenseValues, { ...formOrder, ...values });
  };

  const onPaymentSetupSuccess = () => {
    console.log('Payment setup success');
    // orderForm.submit();
  };

  const requiresPaymentSetup = formOrder?.amount > 0 && !dataSource?.customer.paymentType;

  return (
    <Modal
      title="Select license"
      centered
      open={open}
      width={step > 1 && step < 4 ? 1200 : 800}
      loading={loading}
      footer={step > 1 && step < 4 ? (
        <>
          {isSuperUser && <div style={{ float: 'left' }}><HubspotStatus hubspotDealRecordId={dataSource.hubspotDealRecordId} /></div>}
          <Space>
            {step > minStep && <Button onClick={goBack}>Back</Button>}
            {step === 2 && isSuperUser && (
              <Button onClick={onCustomOrder}>
                Customize order
              </Button>
            )}
            {step === 2 && (
              <Button type="primary" onClick={licenseForm.submit} loading={changeLicenseLoading} disabled={!(licenseValues?.license || licenseValues?.modules?.length) || requiresPaymentSetup}>
                Order
              </Button>
            )}
            {step === 3 && (
              <Button type="primary" onClick={orderForm.submit} loading={changeLicenseLoading}>
                Order
              </Button>
            )}
          </Space>
        </>
      ) : null}
      onCancel={onCancel}
    >
      {step === 1 && (
        <Row gutter={16}>
          <Col span={8}>
            <Card title="E-Space" hoverable onClick={() => onLicenseTypeSelect('legacy')}>
              All in one license
            </Card>
          </Col>
          <Col span={8}>
            <Card title="May 2023" hoverable onClick={() => onLicenseTypeSelect('v1')}>
              Multiple product levels
            </Card>
          </Col>
          <Col span={8}>
            <Card title="Nov 2024" hoverable onClick={() => onLicenseTypeSelect('v2')}>
              Single product level
            </Card>
          </Col>
        </Row>
      )}
      {step === 2 && (
        <>
          {licenseType === 'legacy' && (
            <LegacyLicenseForm form={licenseForm} dataSource={dataSource} onSubmit={onSubmit} onChange={onLicenseChanged} />
          )}
          {licenseType === 'v1' && (
            <ModuleLicenseForm form={licenseForm} dataSource={dataSource} onSubmit={onSubmit} onChange={onLicenseChanged} />
          )}
          {licenseType === 'v2' && (
            <ModuleLicenseV2Form form={licenseForm} dataSource={dataSource} onSubmit={onSubmit} onChange={onLicenseChanged} />
          )}
          {formOrder && <Order order={formOrder} />}
          {requiresPaymentSetup && (
            <Alert
              style={{ marginTop: 16 }}
              message="Setup payment before you can order a paid license."
              type="warning"
              showIcon
              action={<SetupPaymentButton customerId={dataSource?.customer.id} onSuccess={onPaymentSetupSuccess} />}
            />
          )}
        </>
      )}
      {step === 3 && (
        <OrderForm form={orderForm} order={formOrder} canEdit={isSuperUser} onSubmit={onCustomOrderSubmit} />
      )}
      {step === 4 && (
        <Result
          status="success"
          title="License changed"
          subTitle={(
            <p>
              Your license has been updated.
            </p>
          )}
        />
      )}
    </Modal>
  );
};

export default ChangeLicense;
